































































import {_axios} from '@/plugins/axios';
import Vue, {PropType} from 'vue';
import {MyProfileAPI} from '#/User';
import {AxiosError, AxiosResponse} from 'axios';
import ImageResize from '@/components/profile/ImageResize.vue';
import {mapActions} from 'vuex';
import {uuidGenerate} from '@/store';

interface ComponentData {
  dialog: {
    display: boolean;
    id: string;
  };
  onLoad: boolean;
  photo: string;
  idToReset: string;
  updatePictureLoading: boolean;
}

export default Vue.extend({
  name: 'ProfilePicture',

  components: {ImageResize},

  data: (): ComponentData => ({
    dialog: {
      display: false,
      id: '',
    },
    onLoad: true,
    photo: '',
    idToReset: '',
    updatePictureLoading: false,
  }),

  props: {
    user: {
      type: Object as PropType<MyProfileAPI>,
      required: true,
    },
    nameDisplay: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
  },

  computed: {
    hasNoPhoto(): boolean {
      return !this.photo;
    },
    isProfilePage(): boolean {
      return this.$route.name === 'ProfileDetail';
    },
    profileStyle(): { backgroundColor?: string } {
      return this.hasNoPhoto ? {backgroundColor: this.user.profileColor} : {};
    },
  },

  created() {
    this.idToReset = uuidGenerate();
    this.loadProfilePicture();
  },

  methods: {
    ...mapActions([
      'displaySnackBar',
      'updateProfile',
    ]),
    onLoadImage(): Promise<void> {
      return new Promise(resolve => {
        if (this.photo === '') {
          const input = document.createElement('input');
          input.type = 'file';
          input.style.opacity = '0';
          document.querySelector('body')?.appendChild(input);
          input.addEventListener('change', (inputEvent: Event) => {
            const files = (inputEvent.target as HTMLInputElement | undefined)?.files || (inputEvent as InputEvent).dataTransfer?.files;
            if (!files?.length) return;

            let reader = new FileReader();
            reader.onload = (readerEvent) => {
              this.photo = readerEvent.target?.result as string;
              resolve();
            };
            reader.readAsDataURL(files[0]);
          });
          input.click();
        } else {
          resolve();
        }
      });
    },
    onUpdatePicture(): Promise<void> {
      return new Promise(resolve => {
        if (this.$route.name === 'ProfileDetail') {
          this.onLoadImage()
              .then(() => {
                this.dialog.id = uuidGenerate();
                this.dialog.display = true;
                resolve();
              });
        } else {
          resolve();
        }
      });
    },
    onPictureClick(): Promise<void> {
      return new Promise((resolve, reject) => {
        if (this.$route.name !== 'AuthorDetail' && this.$route.name !== 'ProfileDetail') {
          this.$router.push({name: 'AuthorDetail', params: {authorId: `${this.user.id}`}})
              .then(() => {
                resolve();
              })
              .catch(() => {
                reject();
              });
        } else {
          resolve();
        }
      });
    },
    loadProfilePicture(noCache = false): Promise<void> {
      return new Promise((resolve, reject) => {
        this.onLoad = true;
        const headers: { 'Cache-Control'?: 'no-cache' } = {};

        if (noCache) {
          headers['Cache-Control'] = 'no-cache';
        }

        _axios.get(
            `profile/photo/${this.user.id}/`,
            {
              responseType: 'blob',
              headers,
            },
        )
              .then((response: AxiosResponse<Blob>) => {
                const reader = new FileReader();
                reader.readAsDataURL(response.data);
                reader.onload = () => {
                  this.onLoad = false;
                  this.photo = reader.result as string;
                };
                resolve();
              })
              .catch((error: AxiosError) => {
                if (!noCache && error.response?.status === 404) {
                  resolve();
                }
                this.onLoad = false;
                reject(error);
              });
      });
    },
    onSave(newPicture: Blob | undefined): Promise<void> {
      return new Promise<void>((resolve, reject) => {
        this.updatePictureLoading = true;
        const formData = new FormData();
        if (newPicture) formData.set('profile_photo_file', newPicture);

        _axios.patch(
            '/profile/',
            newPicture ? formData : {profile_photo_file: null},
        )
              .then(() => {
                this.loadProfilePicture(true)
                    .then(() => {
                      this.dialog.display = false;
                    })
                    .catch((error: AxiosError) => {
                      if (error.response?.status === 404) {
                        this.dialog.display = false;
                        this.photo = '';
                        this.idToReset = uuidGenerate();
                        this.updatePictureLoading = false;
                        resolve();
                      }

                      this.updatePictureLoading = false;
                      reject();
                    });
              })
              .catch((error: AxiosError) => {
                this.displaySnackBar({
                  text: error.response?.status === 413
                      ? this.$t('profile.picture.errors.tooLarge')
                      : error.response?.data || this.$t('profile.picture.errors.serverError'),
                  type: 'error',
                });
                this.updatePictureLoading = false;
                reject();
              });
      });
    },
  },
});
